import type { DraggableData } from "@/dnd/ids";
import {
  railSelectionKey,
  useRailSelectionOptional,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { buildWorkspacesRailCanvasDragPayload } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/buildWorkspacesRailCanvasDragPayload";
import { WorkspacesRailCanvasRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasRow";
import type { Canvas } from "@cohesive/api-client";
import { useDraggable } from "@dnd-kit/core";
import { useMemo } from "react";

/** Draggable canvas row — must render inside `TransferDndProvider`. */
export function WorkspacesRailDraggableCanvasRow({
  canvas,
  dragNamespace,
  active,
  section = "canonical",
  onRename,
  onMove,
  onShare,
  onDelete,
  expanded,
  onToggleExpanded,
}: {
  canvas: Canvas;
  /** Unique per rail surface so duplicate canvases (e.g. favorites + workspace) don't clash. */
  dragNamespace: string;
  active?: boolean;
  section?: RailSelectionSource;
  onRename: (canvas: Canvas) => void;
  onMove?: (canvas: Canvas) => void;
  onShare?: (canvas: Canvas) => void;
  onDelete?: (canvas: Canvas) => void;
  /** Disclosure state for the canvas's nested chats + files (opt-in). */
  expanded?: boolean;
  onToggleExpanded?: () => void;
}) {
  const selection = useRailSelectionOptional();
  const rowKey = railSelectionKey(section, "canvas", canvas.id);

  const draggableData: DraggableData = useMemo(
    () =>
      buildWorkspacesRailCanvasDragPayload(
        canvas,
        selection?.getSelectedResources() ?? [],
        { rowSelected: !!selection?.isSelected(rowKey) },
      ),
    [canvas, selection, rowKey],
  );

  const draggable = useDraggable({
    id: `rail-canvas:${dragNamespace}:${canvas.id}`,
    data: draggableData,
  });

  return (
    <WorkspacesRailCanvasRow
      canvas={canvas}
      active={active}
      section={section}
      onRename={onRename}
      onMove={onMove}
      onShare={onShare}
      onDelete={onDelete}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      dragRef={draggable.setNodeRef}
      dragProps={{
        ...draggable.attributes,
        ...draggable.listeners,
      }}
      isDragging={draggable.isDragging}
    />
  );
}
